import type { SupabaseClient } from "@supabase/supabase-js";
import { terjemahkanErrorRpc } from "@/lib/rpcError";
import { escapeLikeLiteral } from "@/lib/config";
import { variantPricingFromRow } from "@/lib/backfillUnitPrice";
import type { BrandImpactVariant } from "@/lib/groupBrandImpact";
import { normName } from "@/lib/promo";
import { fetchAllRows } from "@/lib/supabase/fetchAllVariants";
import {
  brandDiscountsForGroup,
  groupBrandRulesFromRows,
  withBrandDiscounts,
  type GroupBrandDiscountRow,
  type GroupBrandRule
} from "@/lib/groupBrandDiscount";
import type { CustomerGroup } from "@/lib/reseller-groups";

// ─── Pemuat aturan diskon merek per level (mig 7070) ────────────────────────
// Lewat RPC `group_brand_discount_rules()` saja (tabelnya tertutup untuk klien):
// sesi admin memulangkan seluruh aturan tokonya, sesi pembeli hanya level aktif
// miliknya; konteks toko kosong = galat 42501 (fail-closed, aturan A2).
//
// SENGAJA tanpa "use client": route server (backfill harga, impor) memanggil
// pemuat yang SAMA dengan sesi admin server — satu pembaca, dua dunia (idiom
// `fetchAllVariants.ts`).

/** Seluruh aturan yang terlihat sesi ini. Galat → THROW (pemanggil memutuskan). */
export async function fetchGroupBrandDiscountRules(db: SupabaseClient): Promise<GroupBrandRule[]> {
  const { data, error } = await db.rpc("group_brand_discount_rules");
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
  return groupBrandRulesFromRows((data as GroupBrandDiscountRow[] | null) ?? []);
}

/**
 * Pemuat aturan ber-MEMO untuk satu proses (loop re-snapshot, impor, layar admin
 * yang menghitung banyak pembeli): RPC dipanggil sekali, galatnya juga diingat
 * — percobaan berikutnya di proses yang sama gagal dengan galat yang sama,
 * bukan diam-diam memakai "tanpa aturan".
 */
export function memoGroupBrandRules(db: SupabaseClient): () => Promise<GroupBrandRule[]> {
  let pending: Promise<GroupBrandRule[]> | null = null;
  return () => {
    if (!pending) pending = fetchGroupBrandDiscountRules(db);
    return pending;
  };
}

/**
 * Sumber aturan KOSONG — HANYA untuk jalur produk Non Web (`is_manual`), yang
 * tak pernah kena aturan merek (`brandDiscountFor` menolaknya). Memuat aturan
 * di sana hanya menambah mode gagal baru tanpa mengubah satu rupiah pun.
 * Pemakaiannya dikunci lint `groupBrandDiscountLint.test.ts`.
 */
export const NON_WEB_NO_BRAND_RULES: () => Promise<GroupBrandRule[]> = () =>
  Promise.resolve([]);

export type GroupRulesOptions = {
  /** Sumber aturan (mis. `memoGroupBrandRules`); absen = panggil RPC langsung. */
  rules?: () => Promise<GroupBrandRule[]>;
};

/**
 * Pasang aturan merek level ke grup pelanggan. Grup null (tanpa level / level
 * nonaktif) tak memanggil RPC sama sekali — pelanggan umum tak mendapat mode
 * gagal baru. Galat RPC → THROW, idiom `fetchCustomerGroup` (bughunt H2-1):
 * jalur uang tak boleh diam-diam menghitung "tanpa aturan" lalu menguncinya.
 */
export async function attachGroupBrandRules(
  db: SupabaseClient,
  group: CustomerGroup,
  opts: GroupRulesOptions = {}
): Promise<CustomerGroup> {
  if (!group) return null;
  const rules = await (opts.rules ? opts.rules() : fetchGroupBrandDiscountRules(db));
  return withBrandDiscounts(group, brandDiscountsForGroup(rules, group.id));
}

// ─── Layar admin Grup Pelanggan › Diskon per Merek (PR 3) ────────────────────

/** Hasil `admin_set_group_brand_discounts` — jumlah perubahan sinkron ber-diff. */
export type GroupBrandSaveResult = { added: number; changed: number; removed: number };

/**
 * Simpan daftar LENGKAP aturan satu level (merek yang tak disebut DIHAPUS —
 * sinkron ber-diff di server). Galat → THROW berpesan Indonesia.
 */
export async function saveGroupBrandDiscounts(
  db: SupabaseClient,
  groupId: string,
  rules: readonly { brandId: string; persen: number }[]
): Promise<GroupBrandSaveResult> {
  const { data, error } = await db.rpc("admin_set_group_brand_discounts", {
    p_group_id: groupId,
    p_rules: rules.map((r) => ({ brandId: r.brandId, persen: r.persen }))
  });
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
  const out = (data ?? {}) as { added?: number; changed?: number; removed?: number };
  return {
    added: Number(out.added ?? 0),
    changed: Number(out.changed ?? 0),
    removed: Number(out.removed ?? 0)
  };
}

/** Kolom varian yang dihargai pratinjau — diskon per-slot WAJIB ikut. */
const IMPACT_VARIANT_COLUMNS =
  "id, product_id, name, price_normal, price_extra, hpp, diskon_mode, diskon_reseller, diskon_agen, diskon_slot3, diskon_slot4, diskon_slot5, diskon_slot6, diskon_slot7, diskon_slot8, diskon_slot9, diskon_slot10, diskon_slot11, diskon_slot12, diskon_slot13, diskon_slot14, diskon_slot15" as const;

/**
 * Varian AKTIF semua produk AKTIF bermerek `brandName` (bukan Non Web) — bahan
 * pratinjau dampak. Merek dicocokkan ternormalisasi (`normName`, cermin
 * `lower(btrim())` SQL): kueri `ilike` mengambil kandidat, saringan klien yang
 * memutuskan. Galat → THROW (pratinjau yang diam-diam kosong terbaca
 * "tak ada produk terdampak").
 */
export async function fetchBrandImpactVariants(
  db: SupabaseClient,
  brandName: string
): Promise<BrandImpactVariant[]> {
  const key = normName(brandName);
  if (!key) return [];
  const products = await fetchAllRows<{
    id: string;
    name: string | null;
    brand: string | null;
    price_normal: number | string | null;
  }>((from, to) =>
    db
      .from("products")
      .select("id, name, brand, price_normal")
      .ilike("brand", `%${escapeLikeLiteral(brandName.trim())}%`)
      .eq("is_manual", false)
      .eq("is_active", true)
      .order("id", { ascending: true })
      .range(from, to)
  );
  const mine = products.filter((p) => normName(p.brand) === key);
  if (!mine.length) return [];
  const productById = new Map(mine.map((p) => [p.id, p] as const));

  const out: BrandImpactVariant[] = [];
  const ids = [...productById.keys()];
  for (let i = 0; i < ids.length; i += 100) {
    const chunk = ids.slice(i, i + 100);
    const rows = await fetchAllRows<Record<string, unknown>>((from, to) =>
      db
        .from("product_variants")
        .select(IMPACT_VARIANT_COLUMNS)
        .in("product_id", chunk)
        .eq("is_active", true)
        .order("id", { ascending: true })
        .range(from, to)
    );
    for (const row of rows) {
      const product = productById.get(row.product_id as string);
      if (!product) continue;
      const { pricing } = variantPricingFromRow(row as Parameters<typeof variantPricingFromRow>[0]);
      // Varian tanpa harga sendiri mewarisi harga produk (lapis terakhir model harga).
      const fallback = product.price_normal === null ? undefined : Number(product.price_normal);
      const hpp = row.hpp === null || row.hpp === undefined ? null : Number(row.hpp);
      out.push({
        productId: product.id,
        productName: product.name ?? "(tanpa nama)",
        pricing: {
          ...pricing,
          price_normal:
            pricing.price_normal ?? (fallback !== undefined && Number.isFinite(fallback) ? fallback : undefined)
        },
        hpp: hpp !== null && Number.isFinite(hpp) ? hpp : null
      });
    }
  }
  return out;
}

/**
 * Merek AKTIF toko untuk pemilih aturan. Beda dari `fetchAdminBrandsFromSupabase`
 * yang menelan galat jadi `[]`: di sini galat → THROW, karena daftar kosong
 * yang sebenarnya gagal akan terbaca "toko tak punya merek".
 */
export async function fetchActiveBrandsForRules(
  db: SupabaseClient
): Promise<{ id: string; name: string }[]> {
  const { data, error } = await db
    .from("product_brands")
    .select("id, name")
    .eq("is_active", true)
    .order("name", { ascending: true });
  if (error) throw new Error(terjemahkanErrorRpc(error).pesan);
  return ((data ?? []) as { id: string; name: string | null }[])
    .filter((b) => b.id && (b.name ?? "").trim())
    .map((b) => ({ id: b.id, name: (b.name ?? "").trim() }));
}
